<script setup lang="ts">
import type { SimpleIcon } from 'simple-icons';
import { siFacebook, siX, siPinterest } from 'simple-icons';
import { CONTACT_URL } from '@shared/app/constants/contactUrl';
import type { FooterNavigation } from '~~/types/footer';

const primaryNav: FooterNavigation[] = [
  {
    text: 'ダイエットニュース・特集',
    items: [
      {
        text: 'ニュース',
        to: '/public/article/news',
      },
      {
        text: 'ダイエット中の疑問',
        to: '/public/article/dietqa',
      },
    ],
  },
  {
    text: 'レシピ',
    items: [
      {
        text: 'ダイエットレシピ',
        to: '/public/article/recipe/top',
      },
    ],
  },
  {
    text: 'スマートフォンアプリの紹介',
    items: [
      {
        text: 'DietPlus Pro',
        to: '/s/service_dietplus',
      },
    ],
  },
];

const secondaryNav: FooterNavigation[] = [
  {
    text: 'サポート',
    items: [
      {
        text: 'お問い合わせ',
        to: CONTACT_URL,
        newWindow: true,
      },
      {
        text: 'サイトのご利用にあたって',
        to: '/s/notice_of_recipe',
      },
      {
        text: 'メールマガジン購読解除',
        to: '/public/magazineUnsubscribe',
      },
    ],
  },
  {
    text: '企業情報',
    items: [
      {
        text: '運営会社',
        to: 'https://fitsplus.co.jp/',
        newWindow: true,
      },
      {
        text: '採用について',
        to: 'https://fitsplus.co.jp/recruit',
        newWindow: true,
      },
      {
        text: '個人情報保護方針',
        to: 'https://fitsplus.co.jp/privacy',
        newWindow: true,
      },
    ],
  },
];

const snsItems: Array<{
  iconData: SimpleIcon;
  to: string;
}> = [
  {
    iconData: siFacebook,
    to: 'https://www.facebook.com/dietplusjp/',
  },
  {
    iconData: siX,
    to: 'https://x.com/Dietplusjp',
  },
  {
    iconData: siPinterest,
    to: 'https://www.pinterest.jp/dietplusjp/',
  },
];
</script>

<template>
  <footer class="pt-8 md:px-0 border-t border-gray-300">
    <div class="max-w-content-width mx-auto px-4">
      <div class="hidden md:flex text-sm">
        <div v-for="item in primaryNav" :key="item.text" class="w-1/4">
          <p class="font-bold text-pink-400 mb-2">{{ item.text }}</p>
          <p
            v-for="subItem in item.items"
            :key="subItem.text"
            class="[&:not(:last-child)]:mb-2"
          >
            <NuxtLink
              :to="subItem.to"
              :external="subItem.external"
              class="text-gray-500 hover:underline"
              :target="subItem.newWindow ? '_blank' : undefined"
              :rel="subItem.newWindow ? 'noopener' : undefined"
              >{{ subItem.text }}</NuxtLink
            >
          </p>
        </div>
      </div>

      <div
        class="gap-8 mb-16 md:pt-6 md:mt-6 md:border-t border-gray-350 text-sm flex max-md:flex-col"
      >
        <div class="grow max-md:text-center">
          <div
            v-for="item in secondaryNav"
            :key="item.text"
            class="flex max-md:flex-col [&:not(:last-child)]:mb-1 max-md:[&:not(:last-child)]:mb-8"
          >
            <p
              class="font-bold mr-5 mb-2 shrink-0 text-gray-500 max-md:mr-0 max-md:mb-3"
            >
              {{ item.text }}
            </p>
            <div
              class="flex flex-wrap gap-4 max-md:gap-x-4 max-md:gap-y-1 max-md:justify-center"
            >
              <NuxtLink
                v-for="subItem in item.items"
                :key="subItem.text"
                :to="subItem.to"
                :external="subItem.external"
                class="text-gray-500 hover:underline"
                :target="subItem.newWindow ? '_blank' : undefined"
                :rel="subItem.newWindow ? 'noopener' : undefined"
                >{{ subItem.text }}</NuxtLink
              >
            </div>
          </div>
        </div>

        <div class="shrink-0 flex max-md:justify-center gap-2 max-md:mt-4">
          <NuxtLink
            v-for="item in snsItems"
            :key="item.iconData.slug"
            :to="item.to"
            target="_blank"
            rel="noopener"
            class="flex items-center justify-center border border-b-2 border-cream-500 rounded-md bg-white w-12 h-12 p-3"
          >
            <svg
              role="img"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              :alt="item.iconData.title"
              class="w-full h-full"
              :style="{ fill: `#${item.iconData.hex}` }"
            >
              <path :d="item.iconData.path" />
            </svg>
          </NuxtLink>
        </div>
      </div>
    </div>

    <div
      class="bg-cream-200 border-t-2 border-gray-300 pb-16 max-md:pb-32 px-4"
    >
      <div
        class="mx-auto max-w-content-width flex justify-between max-md:flex-col max-md:items-center max-md:gap-8 py-4"
      >
        <small class="block text-lg text-gray-500">&copy; FitsPlus Inc.</small>
        <img
          src="~/assets/img/privacy.gif"
          class="w-[100px]"
          alt="プライバシーマーク"
        />
      </div>
    </div>
  </footer>
</template>
